import React from "react";
import { Alert, Box } from "@mui/material";
import HPInput from "@/components/common/Input";
import type { QuestionDetail } from "@/models/Dataset";
import type {
  ScorecardRowResultType,
  ScorecardSourceQuestionKind,
} from "@/models/Scorecard";
import type { useGridMetricSelection } from "@components/charts/scorecard-config/hooks/useGridMetricSelection";
import GridValueOrRollupSelector from "./GridValueOrRollupSelector";

interface QuestionRowResponseSelectionSectionProps {
  questionDetail: QuestionDetail;
  resultType: ScorecardRowResultType;
  sourceQuestionKind: ScorecardSourceQuestionKind | null;
  isGridReady: boolean;
  gridMetric: ReturnType<typeof useGridMetricSelection>;
  onGridValueChange: (valueId: string) => void;
  onRollupNameChange: (name: string) => void;
  label: string;
  onLabelChange: (label: string) => void;
}

/** Detected-result-type alert + (for Single grid) the Response Selection
 *  (Grid Value/Rollup) picker, plus the row's own Row Label field —
 *  extracted from QuestionRowBuilder to keep it under the file-size limit. */
const QuestionRowResponseSelectionSection: React.FC<QuestionRowResponseSelectionSectionProps> = ({
  questionDetail,
  resultType,
  sourceQuestionKind,
  isGridReady,
  gridMetric,
  onGridValueChange,
  onRollupNameChange,
  label,
  onLabelChange,
}) => {
  const labelField = (
    <HPInput
      label="Row Label*"
      value={label}
      onChange={(e) => onLabelChange(e.target.value)}
      placeholder="Row label"
    />
  );

  return (
    <>
      <Alert severity="info" sx={{ py: 0.5, mb: 2 }}>
        Detected result type:{" "}
        <strong>{resultType === "percentage" ? "Percentage" : "Mean"}</strong>
        {sourceQuestionKind === "single_grid" && " (Single grid)"}
      </Alert>

      {sourceQuestionKind === "single_grid" ? (
        <Box sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 1, mb: 2 }}>
          <GridValueOrRollupSelector
            gridMode={gridMetric.gridMode}
            onGridModeChange={gridMetric.handleGridModeChange}
            values={questionDetail.values ?? []}
            selectedGridValueId={gridMetric.selectedGridValueId}
            onGridValueChange={onGridValueChange}
            rollupName={gridMetric.rollupName}
            rollupSelectedLabels={gridMetric.rollupSelectedCodes}
            onRollupNameChange={onRollupNameChange}
            onRollupLabelsChange={gridMetric.setRollupSelectedCodes}
          />
          {isGridReady && <Box sx={{ mt: 2 }}>{labelField}</Box>}
        </Box>
      ) : (
        isGridReady && <Box sx={{ mb: 2 }}>{labelField}</Box>
      )}
    </>
  );
};

export default QuestionRowResponseSelectionSection;
